import React, { memo, useCallback, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import type { GroupTableBody$key } from '@atlassian/jira-relay/src/__generated__/GroupTableBody.graphql.ts';
import type { GroupTableBody_project$key } from '@atlassian/jira-relay/src/__generated__/GroupTableBody_project.graphql.ts';
import { getFlatListKey } from '@atlassian/jira-issue-table-hierarchy/src/common/utils/get-flat-list-key/index.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useHierarchyActions } from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import { ChildRows } from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows/index.tsx';
import { ChildRowsLoader } from '@atlassian/jira-issue-table-hierarchy/src/ui/child-rows-loader/ChildRowsPreloadedQuery.tsx';
import type {
	RenderChildRows,
	RenderRows,
	RenderCreateIssueController,
	RenderDraggableWrapperProps,
} from '@atlassian/jira-issue-table-hierarchy/src/ui/types.tsx';
import {
	FirstGroupRow,
	GroupRow,
} from '@atlassian/jira-issue-table-grouping/src/ui/group-row/FirstGroupRow.tsx';
import type { GroupTableBody_rankField$key } from '@atlassian/jira-relay/src/__generated__/GroupTableBody_rankField.graphql.ts';
import type { GroupTableBody_rules$key } from '@atlassian/jira-relay/src/__generated__/GroupTableBody_rules.graphql';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { useShouldApplyFrozenColumns } from '../../../controllers/use-should-apply-frozen-columns/index.tsx';
import { useScrollStateSelector } from '../../../controllers/scroll-state/index.tsx';
import Rows from '../../rows/Rows.tsx';
import type { Column, RankFieldTerms, TableInjectedCellProps } from '../../../common/types.tsx';
import {
	useIsVirtualizationEnabled,
	useIsReparentingEnabled,
	useSortDirection,
	useSortField,
	useJql,
	useIsFixedTableLayoutEnabled,
	useIsIssueRankDescending,
	useIsHideDoneItemsEnabled,
	useIsIssueCreateEnabled,
	useIsTimelineEnabled,
	useIsGridNavigationEnabled,
	useIsInlineFieldConfigEnabled,
	useIsRelationshipFieldEnabled,
	useCellBorderStyle,
	useIsRemoteLinkedIssuesEnabled,
	useIsEditableSyncLoaded,
	useIsStickyColumnsVisible,
	useIsInlineEditingEnabled,
	useIsAutoUnstickyOnSmallScreenEnabled,
} from '../../../controllers/features/selectors.tsx';
import { RowContextProvider } from '../../rows/row-context/index.tsx';
import { VirtualizedRows } from '../../rows/virtualized-rows-hierarchy/index.tsx';
import {
	ANALYTICS_SOURCE,
	GROUPED_ITEMS_EXPERIENCE,
	GROUPED_LOAD_MORE_EXPERIENCE,
	MAX_COLUMNS,
	ACTION_COLUMNS,
	CHILD_QUERY_PAGE_SIZE,
} from '../../../common/constants.tsx';

import { TableCell } from '../../../common/ui/table-cell/index.tsx';
import { CreateIssueController } from '../../../services/issue-create-mutation/CreateIssueController.tsx';
import { DraggableRowsContainer } from '../../../controllers/draggable-rows/context.tsx';
import { useTimelineCustomComponents } from '../../../controllers/timeline-custom-components/index.tsx';

export type Props = {
	groupResults: GroupTableBody$key;
	columns: Column[];
	loading: boolean;
	tableInjectedProps: TableInjectedCellProps;
	project: GroupTableBody_project$key | null;
	rankField: GroupTableBody_rankField$key | null;
	isConditionalFormattingEnabled: boolean;
	resizingColumnIndex?: number;
	formattingRules?: GroupTableBody_rules$key | null;
	rowInlineCreateCanShowAdditionalFields: boolean;
};

export const GroupTableBodyNonMemoized = ({
	groupResults,
	project,
	columns,
	rankField,
	loading,
	isConditionalFormattingEnabled,
	formattingRules,
	rowInlineCreateCanShowAdditionalFields,
	...tableBodyProps
}: Props) => {
	const ChildIssueCreateRowComponent = useTimelineCustomComponents()?.ChildIssueCreateRow;

	const groupsData = useFragment<GroupTableBody$key>(
		graphql`
			fragment GroupTableBody on JiraSpreadsheetGroupConnection
			@argumentDefinitions(
				isInlineEditingEnabled: { type: "Boolean", defaultValue: true }
				isDensityFull: { type: "Boolean", defaultValue: false }
				isTimelineEnabled: { type: "Boolean!" }
				isPaginating: { type: "Boolean!" }
				projectKeys: { type: "[String!]" }
				projectKey: { type: "String" }
				viewConfigInput: { type: "JiraIssueSearchViewConfigInput" }
				isConditionalFormattingEnabled: { type: "Boolean!" }
				isLinkRelationshipFieldEnabled: { type: "Boolean!" }
				isRemoteLinkedIssuesEnabled: { type: "Boolean!" }
				isEditableSyncLoaded: { type: "Boolean!" }
			) {
				__id
				firstGroup @skip(if: $isPaginating) {
					id
					issues(
						viewConfigInput: $viewConfigInput
						fieldSetsInput: $fieldSetsInput
						scope: $fieldSetsScope
						first: 50
						settings: $settings
					) @connection(key: "firstGroupSection_nativeIssueTable__issues") {
						# eslint-disable-next-line @atlassian/relay/unused-fields
						edges {
							__typename
						}
					}
					...FirstGroupRow
						@arguments(
							isInlineEditingEnabled: $isInlineEditingEnabled
							isTimelineEnabled: $isTimelineEnabled
							isDensityFull: $isDensityFull
							projectKeys: $projectKeys
							projectKey: $projectKey
							viewConfigInput: $viewConfigInput
							isConditionalFormattingEnabled: $isConditionalFormattingEnabled
							isLinkRelationshipFieldEnabled: $isLinkRelationshipFieldEnabled
							isRemoteLinkedIssuesEnabled: $isRemoteLinkedIssuesEnabled
							isEditableSyncLoaded: $isEditableSyncLoaded
						)
				}
				edges {
					node {
						id
						...FirstGroupRow_GroupRow
					}
				}
			}
		`,
		groupResults,
	);

	const projectData = useFragment(
		graphql`
			fragment GroupTableBody_project on JiraProject
			@argumentDefinitions(isConditionalFormattingEnabled: { type: "Boolean!" }) {
				key
				...Rows_project @arguments(isConditionalFormattingEnabled: $isConditionalFormattingEnabled)
			}
		`,
		project,
	);

	const rankFieldData = useFragment(
		graphql`
			fragment GroupTableBody_rankField on JiraJqlFieldWithAliases
			@argumentDefinitions(
				isJSCAliasChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jsc-alias-changes.relayprovider"
				}
			) {
				fieldId
				jqlTerm
				aliases @include(if: $isJSCAliasChangesEnabled)
				...FirstGroupRow_rankField
			}
		`,
		rankField,
	);

	const rankFieldTerms = useMemo<RankFieldTerms>(
		() => ({
			jqlTerm: rankFieldData?.jqlTerm ?? null,
			aliases: rankFieldData?.aliases ? Array.from(rankFieldData.aliases) : null,
		}),
		[rankFieldData],
	);

	const formattingRulesData = useFragment<GroupTableBody_rules$key>(
		graphql`
			fragment GroupTableBody_rules on JiraFormattingRuleConnection {
				...Rows_rules
			}
		`,
		formattingRules,
	);

	const groupConnectionId = groupsData.__id;
	const { populateConnection } = useHierarchyActions();
	const populateConnectionArgs = useMemo(
		() => ({
			parentItemId: 'root',
			itemsConnectionId: groupConnectionId,
			parentConnectionId: groupConnectionId,
			list:
				groupsData?.edges
					// groupId for each group is the same as itemId
					?.map((edge) => edge?.node && { itemId: edge.node.id, key: null, groupId: edge.node.id })
					.filter(Boolean) ?? [],
		}),
		[groupConnectionId, groupsData.edges],
	);
	const prevPopulateConnectionArgs = usePrevious(populateConnectionArgs);

	if (!isEqual(populateConnectionArgs, prevPopulateConnectionArgs)) {
		populateConnection(populateConnectionArgs);
	}

	/**
	 * Passing those values down to ChildRowsLoader as props to avoid direct imports
	 */
	const jql = useJql();
	const sortField = useSortField();
	const sortDirection = useSortDirection();
	const isReparentingEnabled = useIsReparentingEnabled();
	const isTimelineEnabled = useIsTimelineEnabled();
	const isHideDoneItemsEnabled = useIsHideDoneItemsEnabled();
	const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();
	const width = useScrollStateSelector((scrollState) => scrollState.width);
	const isIssueRankDescending = useIsIssueRankDescending();
	const isGridNavigationEnabled = useIsGridNavigationEnabled();
	const isInlineFieldConfigEnabled = useIsInlineFieldConfigEnabled();
	const isIssueCreateEnabled = useIsIssueCreateEnabled();
	const isLinkRelationshipFieldEnabled = useIsRelationshipFieldEnabled();
	const isRemoteLinkedIssuesEnabled = useIsRemoteLinkedIssuesEnabled();
	const isEditableSyncLoaded = useIsEditableSyncLoaded();
	const cellBorderStyle = useCellBorderStyle();
	const isInlineEditingEnabled = fg('jira_timeline_disable_child_inline_editing')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIsInlineEditingEnabled()
		: true;

	let isStickyColumnsVisible = false;
	let isAutoUnstickyOnSmallScreenEnabled = false;
	if (fg('jira_list_frozen_column_bugfix')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isStickyColumnsVisible = useIsStickyColumnsVisible();
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		isAutoUnstickyOnSmallScreenEnabled = useIsAutoUnstickyOnSmallScreenEnabled();
	} else {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { isStickyColumnsVisible: isStickyColumnsVisibleOld } = useShouldApplyFrozenColumns();
		isStickyColumnsVisible = isStickyColumnsVisibleOld;
	}

	const renderDraggableWrapper = useCallback((props: RenderDraggableWrapperProps) => {
		return (
			<DraggableRowsContainer
				issueConnectionId={props.issueConnectionId}
				instanceId={props.instanceId}
			>
				{props.children}
			</DraggableRowsContainer>
		);
	}, []);

	const renderChildRows: RenderChildRows = useCallback(
		(props) => (
			<ChildRows
				{...props}
				projectKey={projectData?.key}
				isGroupingSupported
				childItemsExperience={GROUPED_ITEMS_EXPERIENCE}
				childItemsLoadMoreExperience={GROUPED_LOAD_MORE_EXPERIENCE}
				analyticsSource={ANALYTICS_SOURCE}
				renderChildRowsLoader={(childRowsLoaderProps) => (
					<ChildRowsLoader
						{...childRowsLoaderProps}
						jql={jql}
						width={width}
						sortField={sortField}
						sortDirection={sortDirection}
						columns={columns}
						amountOfColumns={MAX_COLUMNS}
						actionColumns={ACTION_COLUMNS}
						pageSize={CHILD_QUERY_PAGE_SIZE}
						isReparentingEnabled={isReparentingEnabled}
						isTimelineEnabled={isTimelineEnabled}
						isIssueRankDescending={isIssueRankDescending}
						isHideDoneItemsEnabled={isHideDoneItemsEnabled}
						isFixedTableLayoutEnabled={isFixedTableLayoutEnabled}
						rankFieldId={rankFieldData?.fieldId ?? null}
						renderRows={(rowProps) => (
							<Rows
								project={projectData}
								groupContextFieldInput={
									fg('group_by_any_field_sibling_inline_issue_create')
										? props.groupContextField
										: undefined
								}
								formattingRules={formattingRulesData}
								rowInlineCreateCanShowAdditionalFields={rowInlineCreateCanShowAdditionalFields}
								{...rowProps}
							/>
						)}
						isStickyColumnsVisible={isStickyColumnsVisible}
						renderTableCell={(tableCellProps) => <TableCell {...tableCellProps} />}
						renderCreateIssueController={(args) => (
							<CreateIssueController
								{...args}
								groupContextFieldInput={props.groupContextField}
								jql={jql}
								rankFieldTerms={rankFieldTerms}
							/>
						)}
						renderDraggableWrapper={renderDraggableWrapper}
						isConditionalFormattingEnabled={isConditionalFormattingEnabled}
						isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
						ChildIssueCreateRowComponent={ChildIssueCreateRowComponent}
						isLinkRelationshipFieldEnabled={isLinkRelationshipFieldEnabled}
						isRemoteLinkedIssuesEnabled={isRemoteLinkedIssuesEnabled}
						isEditableSyncLoaded={isEditableSyncLoaded}
						cellBorderStyle={cellBorderStyle}
						isInlineEditingEnabled={isInlineEditingEnabled}
					/>
				)}
			/>
		),
		[
			projectData,
			jql,
			width,
			sortField,
			sortDirection,
			columns,
			isReparentingEnabled,
			isTimelineEnabled,
			isIssueRankDescending,
			isHideDoneItemsEnabled,
			isFixedTableLayoutEnabled,
			rankFieldData?.fieldId,
			isStickyColumnsVisible,
			renderDraggableWrapper,
			isConditionalFormattingEnabled,
			isInlineFieldConfigEnabled,
			ChildIssueCreateRowComponent,
			isLinkRelationshipFieldEnabled,
			isRemoteLinkedIssuesEnabled,
			isEditableSyncLoaded,
			cellBorderStyle,
			isInlineEditingEnabled,
			formattingRulesData,
			rowInlineCreateCanShowAdditionalFields,
			rankFieldTerms,
		],
	);

	const renderRows: RenderRows = useCallback(
		(rowProps) => (
			<Rows
				project={projectData}
				formattingRules={formattingRulesData}
				rowInlineCreateCanShowAdditionalFields={rowInlineCreateCanShowAdditionalFields}
				{...rowProps}
			/>
		),
		[projectData, formattingRulesData, rowInlineCreateCanShowAdditionalFields],
	);
	const renderCreateIssueController: RenderCreateIssueController = useCallback(
		(args) => <CreateIssueController {...args} jql={jql} />,
		[jql],
	);

	const numFirstGroupIssues = groupsData?.firstGroup?.issues?.edges?.length;
	const isFirstGroupEmpty = isNil(numFirstGroupIssues) || numFirstGroupIssues === 0;

	const renderGroups = useCallback(
		(isItemVirtual?: (itemId: string) => boolean) =>
			groupsData?.edges?.map(
				(group, index) =>
					group?.node &&
					// We need to render the first group separately because it requires initialization during the table rendering process.
					// Additionally, it bypasses the ChildRowLoader and directly uses ChildRowsPagination, as the data is already available in data.firstGroup.
					(groupsData?.firstGroup &&
					group.node.id === groupsData.firstGroup.id &&
					!isFirstGroupEmpty ? (
						<RowContextProvider
							key={groupsData.firstGroup.id}
							groupId={groupsData.firstGroup.id}
							groupConnectionId={fg('jsc_list_hide_done_items') ? groupConnectionId : undefined}
							{...tableBodyProps}
							columns={columns}
						>
							<FirstGroupRow
								firstGroupRow={groupsData.firstGroup}
								rankField={rankFieldData ?? null}
								groupConnectionId={groupConnectionId}
								loading={loading}
								isVirtual={
									isItemVirtual?.(
										getFlatListKey({
											itemId: groupsData.firstGroup.id,
											groupId: groupsData.firstGroup.id,
										}),
									) ?? true
								}
								isGroupAtTop={index === 0}
								projectKey={projectData?.key}
								isHideDoneItemsEnabled={isHideDoneItemsEnabled}
								isIssueRankDescending={isIssueRankDescending}
								isTimelineEnabled={isTimelineEnabled}
								columns={columns}
								width={width}
								amountOfColumns={MAX_COLUMNS}
								actionColumns={ACTION_COLUMNS}
								pageSize={CHILD_QUERY_PAGE_SIZE}
								childItemsExperience={GROUPED_ITEMS_EXPERIENCE}
								childItemsLoadMoreExperience={GROUPED_LOAD_MORE_EXPERIENCE}
								analyticsSource={ANALYTICS_SOURCE}
								renderRows={renderRows}
								renderCreateIssueController={renderCreateIssueController}
								isIssueCreateEnabled={isIssueCreateEnabled}
								isGridNavigationEnabled={isGridNavigationEnabled}
								renderDraggableWrapper={renderDraggableWrapper}
								isConditionalFormattingEnabled={isConditionalFormattingEnabled}
								isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
								isStickyColumnsVisible={isStickyColumnsVisible}
								isAutoUnstickyOnSmallScreenEnabled={isAutoUnstickyOnSmallScreenEnabled}
								ChildIssueCreateRowComponent={ChildIssueCreateRowComponent}
								isLinkRelationshipFieldEnabled={isLinkRelationshipFieldEnabled}
								isRemoteLinkedIssuesEnabled={isRemoteLinkedIssuesEnabled}
								isEditableSyncLoaded={isEditableSyncLoaded}
								isInlineEditingEnabled={isInlineEditingEnabled}
							/>
						</RowContextProvider>
					) : (
						<RowContextProvider
							key={group.node.id}
							groupId={group.node.id}
							groupConnectionId={fg('jsc_list_hide_done_items') ? groupConnectionId : undefined}
							{...tableBodyProps}
							columns={columns}
							renderChildRows={renderChildRows}
						>
							<GroupRow
								groupRow={group.node}
								groupConnectionId={groupConnectionId}
								isVirtual={
									isItemVirtual?.(
										getFlatListKey({ itemId: group.node.id, groupId: group.node.id }),
									) ?? true
								}
								isGroupAtTop={index === 0}
								projectKey={projectData?.key}
								columns={columns}
								renderChildRows={renderChildRows}
								isIssueCreateEnabled={isIssueCreateEnabled}
								isGridNavigationEnabled={isGridNavigationEnabled}
								isStickyColumnsVisible={isStickyColumnsVisible}
								isAutoUnstickyOnSmallScreenEnabled={isAutoUnstickyOnSmallScreenEnabled}
							/>
						</RowContextProvider>
					)),
			),
		[
			groupsData?.edges,
			groupsData.firstGroup,
			isFirstGroupEmpty,
			groupConnectionId,
			tableBodyProps,
			columns,
			rankFieldData,
			loading,
			projectData?.key,
			isHideDoneItemsEnabled,
			isIssueRankDescending,
			isTimelineEnabled,
			width,
			renderRows,
			renderCreateIssueController,
			isIssueCreateEnabled,
			isGridNavigationEnabled,
			renderDraggableWrapper,
			isConditionalFormattingEnabled,
			isInlineFieldConfigEnabled,
			isStickyColumnsVisible,
			isAutoUnstickyOnSmallScreenEnabled,
			ChildIssueCreateRowComponent,
			isLinkRelationshipFieldEnabled,
			isRemoteLinkedIssuesEnabled,
			isEditableSyncLoaded,
			isInlineEditingEnabled,
			renderChildRows,
		],
	);

	const isVirtualizationEnabled = useIsVirtualizationEnabled();

	return (
		<Box as="tbody" xcss={tableBodyStyles}>
			{isVirtualizationEnabled ? (
				<VirtualizedRows hasSpacers columnsCount={columns.length}>
					{renderGroups}
				</VirtualizedRows>
			) : (
				renderGroups()
			)}
		</Box>
	);
};

const GroupTableBody = componentWithFG(
	'initialise_virtualiser_state_from_relay_store',
	memo(GroupTableBodyNonMemoized),
	GroupTableBodyNonMemoized,
);

export default GroupTableBody;

const tableBodyStyles = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	borderBottom: 'none',
	overflowX: 'hidden',
});
